import { create } from "zustand";

/** A "related help" suggestion: which FAQ item to preselect the next time the dock opens. */
export interface FaqHint {
  /** FAQ item id, "<module>.<slug>" — the `id` field in faq.json. */
  id: string;
  /** Bumps on every suggestion so repeated hints re-trigger the bounce. */
  nonce: number;
}

const AUTO_KEY = "gymviz.faq.auto";

/** "Smart help" preference — defaults to on; survives reloads. Storage can be blocked, so never throw. */
function readAuto(): boolean {
  try {
    return localStorage.getItem(AUTO_KEY) !== "0";
  } catch {
    return true;
  }
}

interface FaqState {
  /**
   * Smart help: when on, error toasts, task pages and open wizards bounce the Help button and
   * preselect the related question, and the panel follows the module you are on. When off,
   * none of that happens — the panel stays exactly as the user left it.
   */
  auto: boolean;
  setAuto: (on: boolean) => void;
  /** Whether the FAQ dock is open. Survives route changes (SPA navigation). */
  open: boolean;
  /** FAQ module being shown, e.g. "members". Null = the all-modules home view. */
  module: string | null;
  /** Pending suggestion — drives the bounce animation and the preselected question. */
  hint: FaqHint | null;
  openFor: (module?: string | null) => void;
  setModule: (module: string) => void;
  goHome: () => void;
  toggle: (module?: string | null) => void;
  close: () => void;
  /** Suggest a FAQ item (e.g. after an error). Doesn't open the dock; makes the Help button bounce. */
  suggest: (id: string) => void;
  /** Called by the dock once it has shown the hinted item. */
  clearHint: () => void;
}

let nonce = 0;

export const useFaqStore = create<FaqState>()((set, get) => ({
  auto: readAuto(),
  setAuto: (on) => {
    try {
      localStorage.setItem(AUTO_KEY, on ? "1" : "0");
    } catch {
      /* preference just won't persist */
    }
    set({ auto: on, ...(on ? {} : { hint: null }) });
  },
  open: false,
  module: null,
  hint: null,
  openFor: (module) => set({ open: true, ...(module ? { module } : {}) }),
  setModule: (module) => set({ module }),
  goHome: () => set({ module: null }),
  // Clicking a module's own Help button while its FAQ is showing closes the dock.
  toggle: (module) => {
    const { open, module: current, hint } = get();
    if (open && !hint && (!module || module === current)) set({ open: false });
    else set({ open: true, ...(module ? { module } : {}) });
  },
  close: () => set({ open: false }),
  suggest: (id) => {
    if (!get().auto) return; // smart help is off — never nudge
    // Already showing that suggestion — don't re-bounce.
    if (get().hint?.id === id) return;
    set({ hint: { id, nonce: ++nonce } });
  },
  clearHint: () => set({ hint: null }),
}));

/** Non-component code (e.g. the toast helper) can suggest help without a hook. */
export const suggestFaq = (id: string) => useFaqStore.getState().suggest(id);
